import { useCallback } from 'react';
import type { KeyboardEvent } from 'react';

/**
 * Drives roving-tabindex keyboard behavior for a set of button elements that together act as a
 * single ARIA radiogroup (role="radiogroup" on the wrapper, role="radio" + aria-checked on each
 * item). Arrow keys move focus AND selection between items, skipping disabled ones; Space/Enter
 * select the focused item. Each item must render with `data-radio-value={value}` so the next
 * target can be located and focused within the same radiogroup.
 */
export function useRovingRadioGroup<T extends string>(
  values: T[],
  selected: T | null,
  onChange: (value: T) => void,
  disabledValues?: Set<T>,
) {
  const getItemProps = useCallback(
    (value: T) => {
      const isDisabled = disabledValues?.has(value) ?? false;
      const isSelected = selected === value;
      const isFirstEnabled = values.find((v) => !(disabledValues?.has(v) ?? false)) === value;

      const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
        if (isDisabled) return;
        let step = 0;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') step = 1;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') step = -1;
        else if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onChange(value);
          return;
        } else {
          return;
        }

        e.preventDefault();
        const startIdx = values.indexOf(value);
        let nextIdx = startIdx;
        for (let attempts = 0; attempts < values.length; attempts++) {
          nextIdx = (nextIdx + step + values.length) % values.length;
          const candidate = values[nextIdx];
          if (!(disabledValues?.has(candidate) ?? false)) {
            onChange(candidate);
            const group = (e.currentTarget as HTMLElement).closest('[role="radiogroup"]');
            const target = group?.querySelector<HTMLElement>(`[data-radio-value="${candidate}"]`);
            target?.focus();
            return;
          }
        }
      };

      return {
        role: 'radio' as const,
        'aria-checked': isSelected,
        tabIndex: isSelected || (selected == null && isFirstEnabled) ? 0 : -1,
        'data-radio-value': value,
        onKeyDown,
      };
    },
    [values, selected, onChange, disabledValues],
  );

  return { getItemProps };
}
